<template>
    <PageSection fixed>
        <PageHeader id="page-header" title="New Request" />
        <div class="card border border-purple-100 p-4 w-6/12 flex-grow-0 min-h-10/12 justify-start">
            <form @submit.prevent>
                <div class="grid grid-cols-2 gap-4 mb-4">
                    <SelectMenu name="content_type" label="Content Type" :items="contentTypes" />
                </div>
                <!-- Supply details -->
                <SupplyRequestForm v-if="values.content_type === 'supply'" class="pb-4"
                    @update:valid="isSupplyFormValid = $event" @update:values="supplyForm = $event" />
                <!-- Book details -->
                <form class="grid grid-cols-[1fr_1fr] gap-4 pb-4"
                    v-if="values.content_type !== 'supply' && values.content_type !== undefined">
                    <template v-for="(inputField, i) in bookFormFieldsExContentType" :key="i">
                        <!-- Render Input if a text input -->
                        <Input v-if="inputField.type === 'text'" :name="inputField.key" :type="inputField.type"
                            v-model="values[inputField.key as keyof BookDetails]" :label="inputField.label"
                            :aria-describedby="errors[inputField.key as keyof BookDetails] && `${inputField.key}-error`"
                            :required="inputField.required" />

                        <!-- Render select menu if select -->
                        <SelectMenu v-if="inputField.type === 'select'" :name="inputField.key" :type="inputField.type"
                            :label="inputField.label" :items="inputField.options" :required="inputField.required"
                            :aria-describedby="errors[inputField.key as keyof BookDetails] && `${inputField.key}-error`" />

                        <!-- Render date selector -->
                        <div v-if="inputField.type === 'date'">
                            <label :for="inputField.key">{{ inputField.label }} <span class="text-red-600"
                                    v-if="inputField.required">*</span></label>
                            <DateInput :id="inputField.key" :title="inputField.label"
                                :error="!errors[inputField.key as keyof BookDetails]"
                                v-model="(values[inputField.key as keyof BookDetails]) as Date"
                                v-if="inputField.type === 'date'"
                                :aria-describedby="errors[inputField.key as keyof BookDetails] && `${inputField.key}-error`" />
                        </div>

                    </template>
                </form>
                <!-- course details  -->
                <div class="flex flex-col gap-4">
					<div v-if="isCourseDetailsRequired && values.content_type === undefined && prefilledCourseId" class="mt-4">
						<p>
							<b>Your selected course from the Courses table has been pre-selected.</b> 
						</p>
					</div>
					<div v-if="isCourseDetailsRequired && values.content_type !== undefined && prefilledCourseId" class="mt-4">
						<p>
							<b>Your selected course from the Courses table has been pre-selected.</b> You can find additional courses below to add this content to if needed. 
						</p>
					</div>
                    <!-- Note: this secction should match half the form width -->
                    <div v-if="isCourseDetailsRequired && values.content_type !== undefined"
                        class="flex flex-col gap-4 w-[calc(50%-0.5rem)]">
						
                        <!-- Course selection -->
                        <MultiSelectMenu v-if="isCourseSearchEnabled" name="selected_courses" label="Courses"
                            itemName="course" :items="coursesSelectItems"
                            :selection-count="Object.keys(courseDetails).length" :loading="isFetchingCourses"
                            :has-more-values="coursesSelectItems.length >= 20"
                            :defaultText="isFetchingCourses ? 'Loading courses...' : 'Select courses'" required
                            canSearch @search="searchForCourses($event)" @add="addCourses([{ id: $event }])"
                            @remove="removeCourses([$event])" />
                    </div>
                    <!-- Note: this secction should fill the form width -->
                    <div class="flex flex-row flex-wrap gap-1" v-if="Object.keys(courseDetails).length > 0 && values.content_type !== undefined">
                        <Tag v-for="(course, key) of courseDetails" variant="action" clearable
                            @clear="removeCourses([key])">
                            {{ course.aggregatedCourseCode }} - {{ findSemesterLabel(semestersOrderedDesc, course)
                            }}
                        </Tag>
                    </div>
                    <!-- Note: this secction should match the form width -->
                    <div class="flex flex-col gap-4">
                        <span v-if="isCourseDetailsRequired && values.content_type !== undefined">{{
                            addCourseText.question }}<a @click="toggleCourseModal(true)"
                                class="text-purple-500 cursor-pointer font-bold">{{
                                    addCourseText.prompt }}</a></span>

                        <!-- Access fields - rendered under courses section -->
                        <div class="grid grid-cols-[1fr_1fr] gap-4" v-if="accessFields.length > 0 && values.content_type !== 'supply' && values.content_type !== undefined">
                            <SelectMenu
                                v-for="field in accessFields"
                                :key="field.key"
                                :name="field.key"
                                :type="field.type"
                                :label="field.label"
                                :items="field.type === 'select' ? field.options : []"
                                :required="field.required"
                                :aria-describedby="errors[field.key as keyof BookDetails] && `${field.key}-error`" />
                        </div>

                        <Button class="btn-primary w-[calc(50%-0.5rem)]" @click="submitRequest()" v-if="values.content_type !== undefined"
                            :loading="isSubmittingSupply"
                            :disabled="((values.content_type === 'supply' && !isSupplyFormValid) || (values.content_type !== 'supply' && (Object.keys(errors).length > 0 || (isCourseDetailsRequired && Object.entries(courseDetails).length === 0))))">Make
                            request</Button>
                    </div>
                </div>
            </form>
            <CourseModal :modal="courseModalFields" :open="isCreatingCourse" @submit="submitCourse($event)"
                @close="toggleCourseModal(false)" @input="setCourseModalFields" />
            <DuplicateWarningDialog v-if="isWarning" :submit="acceptDuplicateWarning" :cancel="cancelDuplicateWarning"
                :duplicatedCourses="duplicatedCourses" :submittedCount="Object.keys(courseDetails).length" />
        </div>
    </PageSection>
</template>

<script setup lang="ts">
import { useForm } from "vee-validate";
import { useRouter } from "vue-router";
import { definePage } from "vue-router/auto";
import Button from "../../../../shared/components/Button.vue";
import MultiSelectMenu from "../../../../shared/components/Form/ControlledMultiSelectMenu.vue";
import DateInput from "../../../../shared/components/Form/Date.vue";
import Input from "../../../../shared/components/Form/Input.vue";
import SelectMenu, {
	SelectMenuItem,
} from "../../../../shared/components/Form/SelectMenu.vue";
import PageSection from "../../../../shared/components/PageSection.vue";
import Tag from "../../../../shared/components/Tag.vue";
import { isFeatureFlagEnabled } from "../../../../shared/lib/posthog";
import PageHeader from "../../components/PageHeader.vue";
import CourseModal from "../../components/Request/CourseModal.vue";
import DuplicateWarningDialog from "../../components/Request/DuplicateWarningDialog.vue";
import SupplyRequestForm from "../../components/Request/SupplyRequestForm.vue";
import {
	findSemesterLabel,
	useCourseSelectItems,
	useTitleRequest,
} from "../../composables/useTitleRequest";
import {
	BookDetails,
	SupplyRequest,
} from "../../state-machines/title-request-form-actor";
import { useAcademicDates } from "../../stores/academic-dates";
import {
	type InstitutionConfig,
	useInstitution,
} from "../../stores/institution";
import { getToastMessage } from "../../utils/request-toast-message";

const institution = useInstitution();
const config = institution.institutionConfig ?? ({} as InstitutionConfig);
const router = useRouter();
const route = useRoute();

// Get semester order by start date descending
const { semestersOrderedDesc } = useAcademicDates();

/**
 * Extract parts from courseId in route query
 * Expected format: ?courseId=START_DATE::COURSE_ID
 * Used immediately for the initial fetch
 */
const courseToInclude = (() => {
	if (!route.query.courseId) {
		return undefined;
	}
	const rawId = decodeURIComponent(route.query.courseId as string | "");

	let id: string;
	let startDate: Date;
	let composedId: string;

	composedId = rawId;

	const parts = rawId.split("::");
	startDate = new Date(parts[0]);
	id = parts[1] ?? "";

	return { id, startDate, composedId };
})();

const prefilledCourseId = ref<string | undefined>(undefined);
const initialCourseIdProcessed = ref(false);

const {
	accessLengths,
	courseSearchResults,
	courseDetails,
	courseModalFields,
	bookFormFields,
	isCourseSearchEnabled,
	isCourseDetailsRequired,
	isFetchingCourses,
	isCreatingCourse,
	searchForCourses,
	addCourses,
	removeCourses,
	toggleCourseModal,
	setCourseModalFields,
	submitCourse,
	setBookDetails,
	submitTitleRequest,
	submitSupplyRequest,
	isWarning,
	isSubmittingSupply,
	duplicatedCourses,
	acceptDuplicateWarning,
	cancelDuplicateWarning,
} = useTitleRequest({
	resetBookFieldsOnSubmit: true,
	include: courseToInclude
		? {
				id: courseToInclude.id,
				startDate: courseToInclude.startDate.toISOString(),
			}
		: undefined,
	requestType: "request_form",
});

const isSupplyFormValid = ref(false);
const supplyForm = ref<Partial<SupplyRequest>>({});

const contentTypes = computed(() => {
	const contentTypeField = bookFormFields.value.inputs.find(
		(input) => input.key === "content_type",
	);
	if (!contentTypeField) return [];
	if ("options" in contentTypeField) {
		const options = [...contentTypeField.options] as SelectMenuItem[];
		if (
			config.enable_supply_request === "enabled" &&
			!options.some((option) => option?.id === "supply")
		) {
			options.push({ id: "supply", label: "Supply" });
		}
		return options;
	}
	return [];
});

const accessFieldKeys = [
	"access_program",
	"requirement_status",
	"access_length",
];

const bookFormFieldsExContentType = computed(() => {
	return bookFormFields.value.inputs.filter(
		(input) =>
			input.key !== "content_type" && !accessFieldKeys.includes(input.key),
	);
});

const accessFields = computed(() => {
	return bookFormFields.value.inputs.filter((input) =>
		accessFieldKeys.includes(input.key),
	);
});

function submitRequest() {
	if (values.content_type === "supply" && isSupplyFormValid.value) {
		submitSupplyRequest(supplyForm.value as SupplyRequest);
	} else {
		const { message, to } = getToastMessage(config.approval_type);
		submitTitleRequest({
			buttonText: "Go",
			message,
			buttonAction: () => {
				router.push(to);
			},
			timeout: 10000,
		});
	}
}

const coursesSelectItems = useCourseSelectItems({
	courseDetails,
	courseSearchResults,
	semestersOrderedDesc,
});

const { values, errors, setFieldValue, setFieldError } = useForm<BookDetails>({
	initialValues: {
		access_length:
			accessLengths.options.find(({ id }) => id === "180")?.id ??
			accessLengths.options[0]?.id ??
			"180",
		access_program: "yes",
		requirement_status: "required",
	},
});

const addCourseText = computed(() => {
	if (isCourseSearchEnabled.value) {
		return {
			question: "Your course isn't listed? ",
			prompt: "Add a new course here",
		};
	}

	return {
		question: "Add a ",
		prompt: "new course here",
	};
});

watch(
	courseSearchResults,
	(results) => {
		if (Object.keys(results).length === 0) return;
		if (initialCourseIdProcessed.value) return;
		if (!courseToInclude) return;

		// Check if course exists in search results
		if (!results[courseToInclude.composedId]) return;

		prefilledCourseId.value = courseToInclude.composedId;
		addCourses([{ id: courseToInclude.composedId }]);
		initialCourseIdProcessed.value = true;
	},
	{ immediate: true },
);

/**
 * Using watch because the select menu do not trigger the 'input' event when select inputs are altered.
 */
watch(values, (newValues) => {
	// The setTimeout might seem pointless but it is necessary to ensure the error messages are updated.
	// Without this, error messages do not display. I can only assume it's because of the vee-validate internals.
	setTimeout(() => {
		setBookDetails(newValues);
	}, 0);
});

/**
 * Sync the props with the form. Note that we aren't using two-way binding because the state machine
 * controls the form values.
 */
watch(bookFormFields, (form) => {
	// Loop through the inputs and set their values and errors. We don't use setValues or setErrors because
	// when we reset the form both objects are empty objects. This means the values and errors are not reset.
	for (const input of form.inputs) {
		// Note: the false is important to prevent vee-validate from clearing error messages.
		setFieldValue(
			input.key as keyof BookDetails,
			form.values[input.key as keyof BookDetails],
			false,
		);
		setFieldError(
			input.key as keyof BookDetails,
			form.errors[input.key as keyof BookDetails],
		);
	}
});

definePage({
	meta: {
		requiresAuth: true,
	},
	alias: ["/requests/new-request", "/booklist/new-request"],
});
</script>
